import {
  buildFromStoredOrCrossSession,
  buildWwwHeartbeatOrEmpty,
  buildWwwSsoUserOrEmpty,
  isCrossSessionParam,
  buildSignupDecorationOrUndefined,
  type CrossSessionParam,
  type CrossSessionResp,
  type UserData,
  type WwwHeartbeatData,
  type WwwSsoUser,
} from '@onshape/www-inertia';
import type { CrossSessionQueryResult } from '~/sanity/types';
import SolrFq from '~/utils/solr/SolrFq';
import SolrQuery from '~/utils/solr/SolrQuery';
import { useHekateEventBus } from './useHekateEventBus';

type LoginStatus = 'unknown' | 'loggedin' | 'loggedout';

const isUserInit: Ref<boolean> = ref(false);
const user: Ref<WwwSsoUser | null> = ref(null);
const heartbeat: Ref<WwwHeartbeatData | null> = ref(null);
const allParams: Ref<Array<CrossSessionParam>> = ref([]);
let fetchTimeout: ReturnType<typeof setTimeout> | null = null;
const isLoggedIn: Ref<LoginStatus> = ref('unknown');

const addHekateListeners = async () => {
  if (import.meta.server) {
    return;
  }
  const { addEventListener } = useHekateEventBus();

  addEventListener('request-sso-info', () => sendToHekate());
};
addHekateListeners();

const sendToHekate = async () => {
  if (import.meta.server) {
    return;
  }
  const data = buildSsoInfoForHekate({ user });
  if (isLoggedIn.value !== 'unknown') {
    data.isReady = true;
  }

  const { dispatchEvent } = useHekateEventBus();
  dispatchEvent('sso-info', { ...data });
};

/** get user data from either sanity or crossSession depending on params */
const getUserData = async () => {
  const fromParams = useRoute().query;
  const env = useEnvContext();

  if (fromParams['sso-data-query'] && (env.isDev || env.isDeveloper)) {
    return getDataFromSanity({
      persona: `${fromParams['sso-data-query']}`,
    });
  }
  return await getDataFromCrossSession();
};

/** Query sanity for mock cross session responses depending on requested persona in params */
const getDataFromSanity = async ({
  persona,
}: {
  persona: string;
}): Promise<CrossSessionQueryResult> => {
  if (persona === 'default') return {} as CrossSessionQueryResult;

  const fq = SolrFq.and().push('_type_s', 'crossSessionResp').push('persona_s', persona);
  const q = SolrQuery.devObj().setFq(fq).setRange({ start: 0, rows: 1 });
  const { data: queryResp } = await useSolrQuery<CrossSessionQueryResult>({
    q,
  });
  if (!queryResp.value || queryResp.value.length === 0) {
    console.warn('No Solr response from sanity for persona', persona);
    return {} as CrossSessionQueryResult;
  }

  return queryResp.value[0];
};

/** fetch current user data from cross session */
const getDataFromCrossSession = async (): Promise<CrossSessionQueryResult> => {
  const crossSessionUrl = new URL(`${useRequestURL().origin}/api/fb/crossSessionV2`);
  allParams.value.forEach((param) => {
    if (!isCrossSessionParam(param)) {
      throw new Error('Invalid SsoReqParam');
    }

    crossSessionUrl.searchParams.append('sso-data-query', param);
  });

  crossSessionUrl.searchParams.append('nonce', newNonce());

  // actually make fetch to crossSession
  const data = await useWwwFetch<CrossSessionResp>({ url: crossSessionUrl, method: 'POST' });

  if (!data.content) throw new Error('No content in crossSession response');
  isLoggedIn.value = data.loginStatus as LoginStatus;

  return data.content as CrossSessionQueryResult;
};

const fetchUserData = async (): Promise<UserData> => {
  const userData: CrossSessionQueryResult = await getUserData();
  const result: UserData = {
    user: null,
    heartbeat: null,
  };

  if (userData?.User) {
    result.user = buildWwwSsoUserOrEmpty(userData.User);
  }

  if (userData?.Heartbeat) {
    result.heartbeat = buildWwwHeartbeatOrEmpty(userData.Heartbeat);
  }

  storeResp({ user: result.user, heartbeat: result.heartbeat });
  return result;
};

/** promise chain to handle debounced fetches */
let promiseChain: Promise<UserData> = Promise.resolve<UserData>({ user: null, heartbeat: null });

/** fetchUserData with debounce and params handling */
const makeAsyncFetch = async (params: Array<CrossSessionParam>) => {
  try {
    promiseChain = promiseChain.then(() => fetchUserData());
    const userData = await promiseChain;

    if (params.includes('heartbeat')) {
      heartbeat.value = userData.heartbeat;
    }

    if (userData.user) {
      if (params.includes('currentUserData')) {
        user.value = userData.user;
      }

      if (params.includes('eloquaExtraData')) {
        const currUser = user.value || buildWwwSsoUserOrEmpty(null);
        user.value = {
          ...currUser,
          eloquaExtraData: userData.user.eloquaExtraData,
        };
      }

      if (params.includes('refCodeData')) {
        const currUser = user.value || buildWwwSsoUserOrEmpty(null);
        user.value = {
          ...currUser,
          refCodeData: userData.user.refCodeData,
        };
      }

      if (params.includes('emailPreferences')) {
        const currUser = user.value || buildWwwSsoUserOrEmpty(null);
        user.value = {
          ...currUser,
          eloquaExtraData: userData.user.eloquaExtraData,
        };
      }

      if (params.includes('contactFields')) {
        const currUser = user.value || buildWwwSsoUserOrEmpty(null);
        user.value = {
          ...currUser,
          eloquaExtraData: userData.user.eloquaExtraData,
        };
      }
    }
    storeResp(userData);
  } catch (error) {
    console.error('useCrossSession error: ', error);
  }
};

/** set params for a fetch request to cross session, debounced */
const setParamsForFetch = ({ params }: { params: Array<CrossSessionParam> }) => {
  if (import.meta.server) {
    return;
  }

  params.forEach((param) => {
    if (!isCrossSessionParam(param)) {
      throw new Error('Invalid CrossSessionParam');
    }

    if (!allParams.value.includes(param)) {
      allParams.value.push(param);
    }
  });

  // Clear the existing timeout if new params are added
  if (fetchTimeout) {
    clearTimeout(fetchTimeout);
  }

  const timeoutTime = 500; // debounce timeout time
  // Set a timeout to delay the fetch
  fetchTimeout = setTimeout(() => {
    makeAsyncFetch(allParams.value)
      .catch((e) => {
        console.error('makeAsyncFetch - Error fetching user data:', e);
      })
      .finally(() => {
        isUserInit.value = true; // Set user init to true after fetch
      });
    fetchTimeout = null; // Reset the timeout after the fetch
  }, timeoutTime);
};

let isInit = false;
const init = () => {
  if (isInit) return;
  isInit = true;

  // if !heartbeat or is too old, fetch heartbeat
  const heartBeatExpires = 1000 * 60 * 60;
  const { heartbeatStorable } = useWwwStorage();
  const heartbeatDataFromStorage = heartbeatStorable.value;
  if (
    !heartbeatDataFromStorage ||
    !heartbeatDataFromStorage.lastFetched ||
    heartbeatDataFromStorage.lastFetched < Date.now() - heartBeatExpires
  ) {
    setParamsForFetch({ params: ['heartbeat'] });
  }
};

//** store user data resp to local storage */
const storeResp = (resp: UserData) => {
  const { setPersonalizationProfile, personalizationProfileStorable } = useWwwStorage();

  const fromStorage = personalizationProfileStorable.value;

  let newDec;
  if (isLoggedIn.value === 'loggedin') {
    newDec = buildSignupDecorationOrUndefined({
      ...fromStorage?.signupDecoration,
      stage: 'have-logged-in',
    });
  }

  const profileToStore = buildFromStoredOrCrossSession({
    fromStorage,
    fromCrossSession: {
      user: resp.user && {
        ...resp.user,
        _id: resp.user._id || '',
        lastFetched: Date.now(),
        signupDecoration: newDec || fromStorage?.signupDecoration,
      },
      heartbeat: resp.heartbeat && { ...resp.heartbeat, lastFetched: Date.now() },
    },
  });

  setPersonalizationProfile({ data: profileToStore });
};

const refreshHeartbeat = () => {
  heartbeat.value = null;
  const { clearHeartbeatData } = useWwwStorage();
  clearHeartbeatData();
  clearParams();
  setParamsForFetch({ params: ['heartbeat'] });
};

const clearParams = () => {
  allParams.value = [];
};

export const useCrossSession = () => {
  init();

  watch(user, () => {
    sendToHekate();
  });

  return {
    isUserInit,
    user,
    heartbeat,
    allParams,
    setParamsForFetch,
    refreshHeartbeat,
  };
};
